/**
 * MICROSCOPIC SCALE - plasma, sheath, and the ion energy distribution.
 *
 * This is the "why" layer for the etch module. It answers, from plasma physics
 * rather than from a lookup, the two questions a technician's power and pressure
 * settings really decide: how many ions arrive at the wafer, and how hard they
 * hit. Its two outputs - ion flux and ion energy - are the parameters the
 * macroscopic reactor model consumes, so this file is literally where the
 * micro-to-macro coupling happens.
 *
 * Relationships used here are the standard low-pressure discharge results
 * (Lieberman and Lichtenberg): Bohm flux with the Godyak edge-to-centre ratio,
 * Child-law sheath thickness, power-balance sheath voltage. Ranges of validity
 * are noted at each step.
 */

import { AMU, ELEM_CHARGE, KB, MASS, mtorrToPa, numberDensity } from './constants'
import { clamp } from '../core/math'

const EPS0 = 8.8541878128e-12
/** Charge-exchange cross-section for a molecular ion in its parent gas [m^2]. */
const SIGMA_CX = 5e-19
/** Effective plasma chamber height used for the edge-to-centre ratio [m]. */
const PLASMA_HEIGHT = 0.08
/** Bias RF drive frequency [Hz] - sets how much the sheath field swings. */
const BIAS_FREQ = 13.56e6

export interface PlasmaState {
  /** Electron temperature [eV]. */
  electronTempEv: number
  /** Bulk plasma density [m^-3]. */
  plasmaDensity: number
  /** Area-averaged ion flux to the wafer [m^-2 s^-1]. */
  ionFlux: number
  /** Mean ion bombardment energy [eV]. */
  ionEnergyEv: number
  /** Time-averaged sheath thickness [m]. */
  sheathThickness: number
  /** Ion mean free path in the sheath [m]. */
  ionMeanFreePath: number
  /** Sheath collisionality s/lambda. Below ~0.3 the sheath is collisionless. */
  collisionality: number
  /** Debye length [m]. */
  debyeLength: number
  /** Fraction of ions that arrive having suffered a charge-exchange collision. */
  collidedFraction: number
  /** Full width of the RF-induced bimodal energy splitting [eV]. */
  rfSplittingEv: number
}

/**
 * Electron temperature from particle balance. Te falls as pressure rises because
 * ionisation only has to make up for slower diffusive losses. The fit below
 * tracks the standard solution over 2-200 mTorr to within a few tenths of an eV,
 * which is well inside what this model needs.
 */
function electronTemperature(pressureMtorr: number): number {
  return 2.0 + 3.5 * Math.exp(-pressureMtorr / 28)
}

/**
 * Godyak edge-to-centre density ratio. At low pressure the plasma is nearly flat
 * and h approaches 0.5-0.6; at high pressure the profile is strongly peaked and
 * less of the bulk density reaches the sheath edge.
 */
function edgeToCentreRatio(ionMfp: number): number {
  return 0.86 / Math.sqrt(3 + PLASMA_HEIGHT / (2 * ionMfp))
}

/**
 * Plasma density per absorbed watt at the reference condition. This is the one
 * fitted number in this file; it is set so that 600 W at 20 mTorr returns about
 * 2e17 m^-3, the middle of the measured range for an inductively coupled
 * fluorocarbon discharge. Every dependence around it is physical.
 *
 * It also sets the ion energy scale, because energy comes from bias power
 * divided by ion current: too few ions and the model reports an unphysically
 * hard bombardment. Calibrated here, 100 W of bias lands near 100 eV.
 */
const IONISATION_YIELD = 3.6e14

export function solvePlasma(opts: {
  pressureMtorr: number
  sourcePowerW: number
  biasPowerW: number
  gasTempK: number
  electrodeAreaM2: number
  sourceMatchEfficiency: number
  biasMatchEfficiency: number
}): PlasmaState {
  const { pressureMtorr, gasTempK, electrodeAreaM2 } = opts
  // Absorbed power, not setpoint power. The two matches drift independently.
  const sourcePowerW = Math.max(opts.sourcePowerW * opts.sourceMatchEfficiency, 1)
  const biasPowerW = Math.max(opts.biasPowerW * opts.biasMatchEfficiency, 0)

  const pressurePa = mtorrToPa(pressureMtorr)
  const gasDensity = numberDensity(pressurePa, gasTempK)

  const electronTempEv = electronTemperature(pressureMtorr)

  // Ion mean free path, charge-exchange dominated.
  const ionMeanFreePath = 1 / Math.max(gasDensity * SIGMA_CX, 1e-12)

  // Density rises with absorbed power, and with pressure because the ionisation
  // cost per electron-ion pair falls as Te drops.
  const energyCostFactor = electronTempEv / 3.0
  const plasmaDensity =
    (IONISATION_YIELD * sourcePowerW * (1 + 0.012 * pressureMtorr)) / energyCostFactor

  // Bohm flux at the sheath edge.
  const bohmVelocity = Math.sqrt((ELEM_CHARGE * electronTempEv) / (MASS.CF3_ION * AMU))
  const hL = edgeToCentreRatio(ionMeanFreePath)
  const ionFlux = hL * plasmaDensity * bohmVelocity

  // Sheath voltage from power balance: the bias supply delivers its power as ion
  // kinetic energy across the sheath. Note the consequence - raising SOURCE
  // power at fixed BIAS power makes ions MORE numerous but LESS energetic,
  // because the same watts are shared among more of them.
  const floatingPotential = 5.2 * electronTempEv
  const ionCurrent = ionFlux * electrodeAreaM2 * ELEM_CHARGE
  const biasVolts = biasPowerW > 0 ? biasPowerW / Math.max(ionCurrent, 1e-9) : 0
  const ionEnergyEv = clamp(biasVolts + floatingPotential, 1, 5000)

  // Child-law sheath thickness.
  const debyeLength = Math.sqrt((EPS0 * electronTempEv) / (ELEM_CHARGE * plasmaDensity))
  const sheathThickness =
    (Math.SQRT2 / 3) * debyeLength * Math.pow((2 * ionEnergyEv) / electronTempEv, 0.75)

  const collisionality = sheathThickness / ionMeanFreePath
  // Beer-Lambert: the chance an ion crosses the sheath without charge exchange.
  const collidedFraction = 1 - Math.exp(-collisionality)

  // RF modulation splits the energy peak. The splitting scales inversely with
  // ion transit time across the sheath - heavy ions at high frequency cannot
  // follow the field and see only the time-averaged potential.
  const ionTransitTime =
    (3 * sheathThickness) /
    Math.sqrt((2 * ELEM_CHARGE * ionEnergyEv) / (MASS.CF3_ION * AMU))
  const rfSplittingEv = clamp(
    ionEnergyEv / (1 + Math.pow(ionTransitTime * BIAS_FREQ * 2 * Math.PI, 2)),
    0,
    ionEnergyEv,
  )

  return {
    electronTempEv,
    plasmaDensity,
    ionFlux,
    ionEnergyEv,
    sheathThickness,
    ionMeanFreePath,
    collisionality,
    debyeLength,
    collidedFraction,
    rfSplittingEv,
  }
}

/**
 * Build a histogram of the ion energy distribution for display. Two features
 * matter pedagogically and both fall out of the state above: the bimodal RF
 * splitting around the mean, and the low-energy tail contributed by ions that
 * charge-exchanged partway across the sheath - which is what makes a
 * high-pressure etch less directional.
 */
export function ionEnergyDistribution(
  state: PlasmaState,
  bins = 64,
): { energies: Float32Array; weights: Float32Array } {
  const eMax = state.ionEnergyEv * 1.6 + 20
  const energies = new Float32Array(bins)
  const weights = new Float32Array(bins)

  const halfSplit = state.rfSplittingEv / 2
  const peakLo = Math.max(state.ionEnergyEv - halfSplit, 0.5)
  const peakHi = state.ionEnergyEv + halfSplit
  const peakWidth = Math.max(state.ionEnergyEv * 0.035, 1.5)
  const ballistic = 1 - state.collidedFraction

  for (let i = 0; i < bins; i++) {
    const e = ((i + 0.5) / bins) * eMax
    energies[i] = e

    const lo = Math.exp(-((e - peakLo) ** 2) / (2 * peakWidth ** 2))
    const hi = Math.exp(-((e - peakHi) ** 2) / (2 * peakWidth ** 2))
    const peaks = ballistic * (lo + hi)

    // Ions born partway down the potential drop arrive with a roughly
    // 1/sqrt(E) weighted spread of lower energies.
    const tail =
      e < state.ionEnergyEv
        ? (state.collidedFraction * 1.4) /
          Math.sqrt(Math.max(e, 1)) /
          Math.sqrt(state.ionEnergyEv)
        : 0

    weights[i] = peaks + tail
  }

  let total = 0
  for (let i = 0; i < bins; i++) total += weights[i]
  if (total > 0) for (let i = 0; i < bins; i++) weights[i] /= total

  return { energies, weights }
}

/** Neutral gas temperature rises with power density; small, but real. */
export function gasTemperature(sourcePowerW: number, pressureMtorr: number): number {
  const heating = (sourcePowerW / 600) * (1 + pressureMtorr / 120)
  return 300 + 90 * Math.min(heating, 3)
}

/** Thermal (Maxwellian) impingement flux of a neutral at density n. */
export function impingementFlux(density: number, tempK: number, massAmu: number): number {
  const v = Math.sqrt((8 * KB * tempK) / (Math.PI * massAmu * AMU))
  return 0.25 * density * v
}
